import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "bootstrap";
import Swal from "sweetalert2";

// ====== ENDPOINTS (AJUSTA SI CAMBIA TU BACKEND) ======
const apiUrlInventario =
  "https://api.checkmark.mx/financiamiento/vehiculos";
const apiUrlGetVenta =
  "https://api.checkmark.mx/obtener/venta/financiamiento";
const apiUrlAccesorios =
  "https://api.checkmark.mx/cliente/ventas/financiamiento/accesorios";

// fallback imagen
const FALLBACK_IMG = "/assets/img/product/noimage.png";

// ====== HELPERS ======
const safeStr = (v) => String(v ?? "").trim();

const normalizar = (s) =>
  safeStr(s)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // quita acentos
    .replace(/\s+/g, " ")
    .trim();

const stripAfterDash = (title = "") => {
  const t = safeStr(title);

  // corta en " - " (recomendado)
  const idx = t.indexOf(" - ");
  if (idx !== -1) return t.slice(0, idx).trim();

  // fallback: si viene con guion pero sin espacios: "Titulo-KWID"
  const idx2 = t.indexOf("-");
  if (idx2 !== -1) return t.slice(0, idx2).trim();

  return t;
};

const onlyFolio = (s) =>
  safeStr(s)
    .toUpperCase()
    .replace(/[^A-Z0-9_-]/g, "");

const parseMaybeJSON = (v) => {
  try {
    return typeof v === "string" ? JSON.parse(v) : v;
  } catch {
    return null;
  }
};

const getVentaData = (row) => {
  // marca/modelo
  const marca =
    row?.marca ||
    row?.marca_vehiculo ||
    row?.vehiculo_marca ||
    row?.brand ||
    "";

  const modelo =
    row?.modelo_vehiculo ||
    row?.modelo ||
    row?.vehiculo_modelo ||
    row?.model ||
    row?.version ||
    "";

  // tipo_auto (para detectar seminuevo/nuevo)
  const tipoAuto =
    row?.tipo_auto ||
    row?.tipo ||
    row?.tipo_vehiculo ||
    row?.type || // a veces "seminuevos"
    "";

  return {
    marca: safeStr(marca),
    modelo: safeStr(modelo),
    tipoAuto: safeStr(tipoAuto),
    marcaNorm: normalizar(marca),
    modeloNorm: normalizar(modelo),
    tipoAutoNorm: normalizar(tipoAuto),
  };
};

export default function AddonsInventoryByFolio() {
  // si existe token, lo manda; si NO existe, no bloquea ni muestra nada
  const accessToken =
    "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3";

  const [folio, setFolio] = useState("");
  const [venta, setVenta] = useState(null);

  const [loadingVenta, setLoadingVenta] = useState(false);
  const [loadingInv, setLoadingInv] = useState(false);

  const [inventario, setInventario] = useState([]);
  const [savingId, setSavingId] = useState(null);

  const [busqueda, setBusqueda] = useState("");

  const palabrasClaveExcluir = useMemo(
    () =>
      [
        "cobertura",
        "garantia",
        "garantía",
        "extension",
        "extensión",
        "seguro",
        "poliza",
        "póliza",
        "proteccion",
        "protección",
        "assist",
        "asistencia",
      ].map((x) => normalizar(x)),
    [],
  );

  // modal refs
  const folioModalRef = useRef(null);
  const folioModalInstanceRef = useRef(null);
  const folioInputRef = useRef(null);

  useEffect(() => {
    document.body.classList.add("addons-kiosk");
    return () => document.body.classList.remove("addons-kiosk");
  }, []);

  useEffect(() => {
    if (!folioModalRef.current) return;

    folioModalInstanceRef.current = Modal.getOrCreateInstance(
      folioModalRef.current,
      {
        backdrop: "static",
        keyboard: false,
        focus: true,
      },
    );

    folioModalInstanceRef.current.show();
    setTimeout(() => folioInputRef.current?.focus?.(), 150);
  }, []);

  const buildPayload = (obj) =>
    accessToken ? { ...obj, access_token: accessToken } : obj;

  const fetchVentaByFolio = async (folioValue) => {
    setLoadingVenta(true);
    setVenta(null);

    try {
      const resp = await fetch(apiUrlGetVenta, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(buildPayload({ folio_venta: folioValue })),
      });

      const json = await resp.json();
      const row = json?.data?.resultados?.[0] || null;

      if (!row?.id) {
        Swal.fire(
          "No encontrada",
          "No existe una venta con ese folio.",
          "warning",
        );
        return null;
      }

      setVenta(row);
      return row;
    } catch (e) {
      Swal.fire("Error", "No se pudo consultar la venta.", "error");
      return null;
    } finally {
      setLoadingVenta(false);
    }
  };

  const fetchInventarioAddons = async (row) => {
    setLoadingInv(true);
    setInventario([]);

    try {
      const ag_id = row?.ag_id || row?.agency_id || "";
      const ventaData = getVentaData(row);

      if (!ag_id || !ventaData.marca) {
        Swal.fire(
          "Faltan datos",
          "La venta no trae ag_id o marca. Revisa tu API de obtener/venta/financiamiento.",
          "warning",
        );
        return [];
      }

      const resp = await fetch(apiUrlInventario, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(
          buildPayload({
            marca: ventaData.marca,
            ag_id,
          }),
        ),
      });

      const data = await resp.json();

      const food = Array.isArray(data?.food)
        ? data.food
        : Array.isArray(data?.data?.food)
          ? data.data.food
          : [];

      // solo accesorios + garantías
      const addons = food.filter(
        (it) =>
          it &&
          (it.type === "accesorios" || it.type === "garantias") &&
          !it.hide,
      );

      const normalized = addons.map((it, idx) => ({
        id: it.id ?? `${it.type}_${it.title}_${idx}`,
        type: it.type, // accesorios | garantias
        title: it.title || "Sin título",
        modelo: it.modelo || it.description || "", // como tu data
        // price se mantiene interno aunque no lo mostramos
        price: Number(it.price || 0),
        image: it.image || "",
        raw: it,
      }));

      setInventario(normalized);

      if (normalized.length === 0) {
        Swal.fire(
          "Sin resultados",
          "No encontré accesorios/garantías para esa agencia/marca.",
          "info",
        );
      }

      return normalized;
    } catch (e) {
      Swal.fire("Error", "No se pudo cargar el inventario.", "error");
      return [];
    } finally {
      setLoadingInv(false);
    }
  };

  const ensurePlanCliente = (planes) => {
    const list = Array.isArray(planes) ? [...planes] : [];
    let plan = list.find((p) => p?.title === "Plan cliente");

    if (!plan) {
      plan = {
        title: "Plan cliente",
        accessories: [],
        totalPrice: 0,
        isSelected: false,
      };
      list.push(plan);
    }

    if (!Array.isArray(plan.accessories)) plan.accessories = [];
    return { list, plan };
  };

  const addItemToPlanCliente = async (item) => {
    if (!venta?.id || !folio) {
      Swal.fire("Sin venta", "Primero selecciona un folio válido.", "warning");
      return;
    }

    setSavingId(item.id);

    try {
      // 1) Guardar en servidor (tu servicio nuevo)
      const serverOk = await sendAccessoryToServer(item);

      if (!serverOk) {
        Swal.fire(
          "Error",
          "No se pudo guardar el accesorio en el servidor",
          "error",
        );
        return;
      }

      // 2) Actualizar UI local (Plan cliente)
      const { planes, plan } = (() => {
        const planesRaw =
          venta?.planes_financiamiento ||
          venta?.planesFinanciamiento ||
          venta?.planes ||
          "[]";

        const list = Array.isArray(planesRaw)
          ? [...planesRaw]
          : parseMaybeJSON(planesRaw) || [];

        let planCliente = list.find((p) => p?.title === "Plan cliente");

        if (!planCliente) {
          planCliente = {
            title: "Plan cliente",
            accessories: [],
            totalPrice: 0,
            isSelected: false,
          };
          list.push(planCliente);
        }

        if (!Array.isArray(planCliente.accessories))
          planCliente.accessories = [];

        return { planes: list, plan: planCliente };
      })();

      const exists = plan.accessories.some(
        (a) =>
          normalizar(a?.title) === normalizar(item.title) &&
          normalizar(a?.type) === normalizar(item.type),
      );

      if (!exists) {
        plan.accessories.push({
          title: item.title,
          description: item.modelo || "",
          price: Number(item.price || 0),
          type: item.type,
          image: item.image || "", // <- UI lo usa para mostrar imagen
        });

        plan.totalPrice = plan.accessories.reduce(
          (sum, acc) => sum + Number(acc.price || 0),
          0,
        );

        setVenta((prev) => ({ ...prev, planes_financiamiento: planes }));
      }

      Swal.fire("Listo", "Agregado a Plan cliente.", "success");
      const fresh = await fetchVentaByFolio(folio);
      if (fresh) setVenta(fresh);
    } catch (e) {
      Swal.fire(
        "Error",
        e?.message || "No se pudo agregar al Plan cliente.",
        "error",
      );
    } finally {
      setSavingId(null);
    }
  };

  const onConfirmFolio = async () => {
    const raw = folioInputRef.current?.value || "";
    const cleaned = onlyFolio(raw);

    if (!cleaned || cleaned.length < 4) {
      Swal.fire("Folio inválido", "Escribe un folio válido.", "warning");
      return;
    }

    setFolio(cleaned);

    const row = await fetchVentaByFolio(cleaned);
    if (!row) return;

    await fetchInventarioAddons(row);
    folioModalInstanceRef.current?.hide?.();
  };

  const ventaData = useMemo(
    () => (venta ? getVentaData(venta) : null),
    [venta],
  );

  // ====== FILTROS
  const accesoriosGarantias = useMemo(() => {
    return {
      accesorios: inventario.filter((x) => x.type === "accesorios"),
      garantias: inventario.filter((x) => x.type === "garantias"),
    };
  }, [inventario]);

  // Busca imagen en inventario si en BD no viene (match por title+type)
  const resolveImageFromInventario = (acc) => {
    const t = normalizar(stripAfterDash(acc?.title || ""));
    const ty = normalizar(acc?.type || "");
    const found = inventario.find((it) => {
      return (
        normalizar(stripAfterDash(it.title)) === t && normalizar(it.type) === ty
      );
    });
    return found?.image || "";
  };

  // ACCESORIOS
  const accesoriosFiltrados = useMemo(() => {
    const busquedaTexto = normalizar(busqueda);

    const tipo = ventaData?.tipoAutoNorm || "";
    const modelo = ventaData?.modeloNorm || "";

    const esSeminuevo =
      tipo.includes("seminuevo") || tipo.includes("seminuevos");
    const esNuevo = tipo.includes("nuevo") || tipo.includes("nuevos");

    return accesoriosGarantias.accesorios.filter((item) => {
      const titulo = normalizar(item.title);

      const coincideBusqueda = titulo.includes(busquedaTexto);

      const coincideExclusion = palabrasClaveExcluir.some((palabra) =>
        titulo.includes(palabra),
      );

      const coincideModeloONombreTodos =
        (modelo && titulo.includes(modelo)) || titulo.includes("todos");

      // Para seminuevos: excluye si palabra clave y además solo muestra si modelo o "todos"
      if (esSeminuevo) {
        if (coincideExclusion) return false;
        if (!coincideModeloONombreTodos) return false;
      }

      // Para nuevos: solo muestra si modelo o "todos"
      if (esNuevo && !coincideModeloONombreTodos) return false;

      return coincideBusqueda;
    });
  }, [
    accesoriosGarantias.accesorios,
    busqueda,
    palabrasClaveExcluir,
    ventaData?.tipoAutoNorm,
    ventaData?.modeloNorm,
  ]);

  // GARANTÍAS
  const garantiasFiltradas = useMemo(() => {
    const esSeminuevo =
      (ventaData?.tipoAutoNorm || "").includes("seminuevo") ||
      (ventaData?.tipoAutoNorm || "").includes("seminuevos");

    const busq = normalizar(busqueda);

    return accesoriosGarantias.garantias.filter((item) => {
      const titulo = normalizar(item.title);
      const coincideBusqueda = titulo.includes(busq);

      const coincideExclusion = palabrasClaveExcluir.some((palabra) =>
        titulo.includes(normalizar(palabra)),
      );

      // Solo aplica filtro por palabras si es seminuevo
      if (esSeminuevo && coincideExclusion) return false;

      return coincideBusqueda;
    });
  }, [
    accesoriosGarantias.garantias,
    busqueda,
    palabrasClaveExcluir,
    ventaData?.tipoAutoNorm,
  ]);

  // ====== UI helper de imagen ======
  const Img = ({ src, alt }) => (
    <img
      src={src || FALLBACK_IMG}
      alt={alt}
      onError={(e) => (e.currentTarget.src = FALLBACK_IMG)}
      style={{
        width: 54,
        height: 54,
        borderRadius: 10,
        objectFit: "cover",
        border: "1px solid #eee",
        background: "#fafafa",
        flexShrink: 0,
      }}
    />
  );

  const getPlanClienteFromVenta = (venta) => {
    const planesRaw =
      venta?.planes_financiamiento ||
      venta?.planesFinanciamiento ||
      venta?.planes ||
      "[]";

    const planes = Array.isArray(planesRaw)
      ? planesRaw
      : parseMaybeJSON(planesRaw) || [];
    const plan = planes.find((p) => p?.title === "Plan cliente") || null;
    return { planes, plan };
  };

  const getTotalPricePlanCliente = (venta) => {
    const { plan } = getPlanClienteFromVenta(venta);
    if (!plan?.accessories || !Array.isArray(plan.accessories)) return 0;
    return plan.accessories.reduce((sum, a) => sum + Number(a?.price || 0), 0);
  };

  const getPrecioVehiculo = (venta) =>
    Number(
      venta?.precio_vehiculo ?? venta?.precio ?? venta?.vehicle_price ?? 0,
    );

  const getTotalGlobal = (venta, extra = 0) => {
    const base = getPrecioVehiculo(venta);
    const acc = getTotalPricePlanCliente(venta); // suma accessories del Plan cliente
    return base + acc + Number(extra || 0);
  };

  const seleccionadosPlanCliente = useMemo(() => {
    if (!venta) return [];

    const { plan } = getPlanClienteFromVenta(venta);
    const list = Array.isArray(plan?.accessories) ? plan.accessories : [];

    return list.map((acc, idx) => {
      const cleanTitle = stripAfterDash(acc?.title || "");
      return {
        id: acc?.id ?? `${acc?.type || "item"}_${cleanTitle}_${idx}`,
        type: acc?.type || "", // accesorios | garantias
        title: cleanTitle,
        description: acc?.description || "",
        price: Number(acc?.price || 0),
        image: acc?.image || resolveImageFromInventario(acc) || "",
        raw: acc,
      };
    });
  }, [venta, inventario]);

  const sendAccessoryToServer = async (item) => {
    const folioVenta = folio;
    if (!folioVenta) return false;

    const itemPrice = Number(item.price || 0);

    const newTotal = getTotalGlobal(venta, itemPrice); // vehículo + accesorios + item nuevo

    const accessoryItem = {
      title: item.title,
      price: itemPrice.toFixed(2),
      description: item.modelo || item.description || "",
      type: item.type,
    };

    const payload = buildPayload({
      folio_venta: folioVenta,
      nuevo_accesorio: accessoryItem,
      precio_total: newTotal,
    });

    try {
      const response = await fetch(apiUrlAccesorios, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const json = await response.json().catch(() => null);
      const backendOk =
        response.ok && (json?.data?.type === "OK" || json?.statusCode === 200);

      return backendOk;
    } catch (err) {
      return false;
    }
  };

  return (
    <div style={{ padding: 18 }}>
      {/* Header simple (sin menú, sin sesión) */}
      <div className="d-flex align-items-start justify-content-between mb-3 gap-2">
        <div style={{ minWidth: 0 }}>
          <h4 style={{ margin: 0 }}>Accesorios y Garantías</h4>
          <div className="black-text" style={{ fontSize: 13, opacity: 0.8 }}>
            Folio: <strong>{folio || "—"}</strong>
            {venta?.id ? (
              <>
                {" "}
                · Venta ID: <strong>{venta.id}</strong>
                {ventaData?.marca ? (
                  <>
                    {" "}
                    · <strong>{ventaData.marca}</strong>
                    {ventaData?.modelo ? (
                      <>
                        {" "}
                        <strong>{ventaData.modelo}</strong>
                      </>
                    ) : null}
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        </div>

        <div className="d-flex gap-2">
          <button
            className="btn btn-outline-secondary btn-primary"
            onClick={() => folioModalInstanceRef.current?.show?.()}
          >
            Cambiar folio
          </button>

          <button
            className="btn btn-outline-primary"
            onClick={async () => {
              if (!folio) return folioModalInstanceRef.current?.show?.();
              const row = await fetchVentaByFolio(folio);
              if (row) await fetchInventarioAddons(row);
            }}
            disabled={loadingVenta || loadingInv}
          >
            {loadingInv ? "Cargando..." : "Recargar"}
          </button>
        </div>
      </div>

      {/* Buscador (para que aplique igual que tu filtro) */}
      <div className="mb-3">
        <input
          className="form-control"
          placeholder="Buscar accesorio o garantía..."
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
        />
      </div>

      {(loadingVenta || loadingInv) && (
        <div className="alert alert-info">
          {loadingVenta ? "Consultando venta..." : "Cargando inventario..."}
        </div>
      )}

      {!!folio && !loadingInv && (
        <div className="row">
          {/* Accesorios */}
          <div className="col-lg-6 mb-3">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <strong>Accesorios</strong>
                <span className="badge bg-secondary black-text">
                  {accesoriosFiltrados.length}
                </span>
              </div>

              <div
                className="card-body"
                style={{ maxHeight: 540, overflow: "auto" }}
              >
                {accesoriosFiltrados.length === 0 ? (
                  <div style={{ opacity: 0.7 }}>
                    No hay accesorios con ese filtro.
                  </div>
                ) : (
                  accesoriosFiltrados.map((it) => (
                    <div
                      key={it.id}
                      className="d-flex align-items-start justify-content-between"
                      style={{
                        padding: "10px 0",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <div className="d-flex gap-2" style={{ minWidth: 0 }}>
                        <Img src={it.image} alt={it.title} />

                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {stripAfterDash(it.title, ventaData?.modelo)}
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              opacity: 0.75,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {it.modelo || "—"}
                          </div>
                          {/*  no mostramos precio */}
                        </div>
                      </div>

                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => addItemToPlanCliente(it)}
                        disabled={savingId === it.id}
                      >
                        {savingId === it.id ? "Agregando..." : "Agregar"}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Garantías */}
          <div className="col-lg-6 mb-3">
            <div className="card">
              <div className="card-header d-flex justify-content-between align-items-center">
                <strong>Garantías</strong>
                <span className="badge bg-secondary black-text">
                  {garantiasFiltradas.length}
                </span>
              </div>

              <div
                className="card-body"
                style={{ maxHeight: 540, overflow: "auto" }}
              >
                {garantiasFiltradas.length === 0 ? (
                  <div style={{ opacity: 0.7 }}>
                    No hay garantías con ese filtro.
                  </div>
                ) : (
                  garantiasFiltradas.map((it) => (
                    <div
                      key={it.id}
                      className="d-flex align-items-start justify-content-between"
                      style={{
                        padding: "10px 0",
                        borderBottom: "1px solid #eee",
                      }}
                    >
                      <div className="d-flex gap-2" style={{ minWidth: 0 }}>
                        <Img src={it.image} alt={it.title} />

                        <div style={{ minWidth: 0 }}>
                          <div
                            style={{
                              fontWeight: 700,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {it.title}
                          </div>
                          <div
                            style={{
                              fontSize: 13,
                              opacity: 0.75,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {it.modelo || "—"}
                          </div>
                          {/* no mostramos precio */}
                        </div>
                      </div>

                      <button
                        className="btn btn-sm btn-primary"
                        onClick={() => addItemToPlanCliente(it)}
                        disabled={savingId === it.id}
                      >
                        {savingId === it.id ? "Agregando..." : "Agregar"}
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="col-12">
            <div className="alert alert-secondary mb-0">
              Los elementos se agregan directamente al{" "}
              <strong>Plan cliente</strong>.
            </div>
          </div>
        </div>
      )}

      {/* Seleccionados (Plan cliente) */}
      <div className="col-12 mt-3">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <strong>Seleccionados (Plan cliente)</strong>
            <span className="badge bg-secondary black-text">
              {seleccionadosPlanCliente.length}
            </span>
          </div>

          <div
            className="card-body"
            style={{ maxHeight: 320, overflow: "auto" }}
          >
            {seleccionadosPlanCliente.length === 0 ? (
              <div style={{ opacity: 0.7 }}>
                Aún no hay accesorios/garantías guardados en este folio.
              </div>
            ) : (
              seleccionadosPlanCliente.map((it) => (
                <div
                  key={it.id}
                  className="d-flex align-items-start justify-content-between"
                  style={{ padding: "10px 0", borderBottom: "1px solid #eee" }}
                >
                  <div className="d-flex gap-2" style={{ minWidth: 0 }}>
                    <Img src={it.image} alt={it.title} />
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          fontWeight: 700,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {it.title}
                      </div>

                      <div
                        style={{
                          fontSize: 13,
                          opacity: 0.75,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {it.description || "—"}
                      </div>

                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        {it.type === "garantias" ? "Garantía" : "Accesorio"}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal pedir folio */}
      <div
        className="modal fade"
        tabIndex="-1"
        ref={folioModalRef}
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Ingresar folio</h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  if (!folio) return; // si no hay folio, no dejes cerrar
                  folioModalInstanceRef.current?.hide?.();
                }}
              />
            </div>

            <div className="modal-body">
              <div style={{ fontSize: 13, opacity: 0.85, marginBottom: 10 }}>
                Escribe el folio de la venta a la que se le agregarán accesorios
                y garantías.
              </div>

              <input
                ref={folioInputRef}
                className="form-control"
                placeholder="Ej: ABC123XYZ"
                defaultValue={folio}
                onKeyDown={(e) => {
                  if (e.key === "Enter") onConfirmFolio();
                }}
              />
            </div>

            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => {
                  if (!folio) return;
                  folioModalInstanceRef.current?.hide?.();
                }}
              >
                Cancelar
              </button>
              <button className="btn btn-primary" onClick={onConfirmFolio}>
                Usar folio
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
